import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, iif, of } from 'rxjs';
import { map, mergeMap, withLatestFrom, catchError } from 'rxjs/operators';

/* notification service */
import { NotificationService } from 'src/app/features/shared/services/notification-service/notification.service';

/* jsonata object class */
import { JSONAtaObject } from 'src/app/features/shared/helper-classes';

/* lodash lib */
import * as lodash from 'lodash';

/* DataStorage interface for ngrx */
import { DataStorage } from 'src/app/features/data-stores/data-storage.interface';

/* navigate action */
import { Navigate } from 'src/app/features/data-stores/router-data-store/state/router-data-store.actions';

/* Workflow Management API Service */
import { WorkflowManagementControllerService } from 'src/app/features/shared/sdk/services/workflow-management-controller.service';

/* rooming-per-day-data-store actions */
import * as RoomingPerDayDataStoreActions from 'src/app/features/data-stores/rooming-per-day-data-store/state/rooming-per-day-data-store.actions';

/* api services */
import { RoomingPerDayControllerService } from 'src/app/features/shared/sdk_v1/services';

@Injectable()
export class RoomingPerDayDataStoreEffects {
  constructor(
    private actions$: Actions,
    private store: Store<DataStorage>,
    private notificationService: NotificationService,
    private workflowManagementApi: WorkflowManagementControllerService,
    private roomingPerDayApiService: RoomingPerDayControllerService,
  ) { 
    // prettier-ignore
    // #region customCode: data-store-effects__constructor__start
    
    // #endregion customCode: data-store-effects__constructor__start

    // prettier-ignore
    // #region customCode: data-store-effects__constructor__end
    
    // #endregion customCode: data-store-effects__constructor__end
  }

  getRoomingPerDays$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomingPerDayDataStoreActions.GetRoomingPerDays),
      mergeMap((action) => {
        return this.roomingPerDayApiService.find().pipe(
          mergeMap((roomingPerDays) => {
            const payload: any = {
              roomingPerDays: roomingPerDays,
            };
            return [
              RoomingPerDayDataStoreActions.GetRoomingPerDaysSuccessful({
                payload,
              }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while fetching data.',
            );
            return [RoomingPerDayDataStoreActions.GetRoomingPerDaysFailure()];
          }),
        );
      }),
    ),
  );

  getRoomingPerDayById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomingPerDayDataStoreActions.GetRoomingPerDayById),
      mergeMap((action) => {
        return this.roomingPerDayApiService
          .findById({
            id: action.payload.id,
          })
          .pipe(
            mergeMap((roomingPerDay) => {
              const payload: any = {
                roomingPerDay: roomingPerDay,
              };
              return [
                RoomingPerDayDataStoreActions.GetRoomingPerDayByIdSuccessful({
                  payload,
                }),
              ];
            }),
            catchError((error) => {
              this.notificationService.createNotification(
                'error',
                'Failure',
                'There was an error while fetching data.',
              );
              return [
                RoomingPerDayDataStoreActions.GetRoomingPerDayByIdFailure(),
              ];
            }),
          );
      }),
    ),
  );

  getActiveRoomingPerDayWithRelationsById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        RoomingPerDayDataStoreActions.GetActiveRoomingPerDayWithRelationsById,
      ),
      mergeMap((action) => {
        return this.roomingPerDayApiService
          .findById({
            id: action.payload.id,
          })
          .pipe(
            mergeMap((roomingPerDay) => {
              const payload: any = {
                activeRoomingPerDay: roomingPerDay,
              };
              return [
                RoomingPerDayDataStoreActions.GetActiveRoomingPerDayByIdWithRelationsSuccessful(
                  { payload },
                ),
              ];
            }),
            catchError((error) => {
              this.notificationService.createNotification(
                'error',
                'Failure',
                'There was an error while fetching data.',
              );
              return [
                RoomingPerDayDataStoreActions.GetActiveRoomingPerDayByIdWithRelationsFailure(),
              ];
            }),
          );
      }),
    ),
  );

  createRoomingPerDay$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomingPerDayDataStoreActions.CreateRoomingPerDay),
      mergeMap((action) => {
        const params = {
          body: action.payload.roomingPerDay,
        };
        return this.roomingPerDayApiService.create(<any>params).pipe(
          mergeMap((roomingPerDay) => {
            this.notificationService.createNotification(
              'success',
              'Successful',
              'Your data has been saved successfully.',
            );
            const payload: any = {
              roomingPerDay: roomingPerDay,
            };
            return [
              RoomingPerDayDataStoreActions.CreateRoomingPerDaySuccessful({
                payload,
              }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while saving the data.',
            );
            return [RoomingPerDayDataStoreActions.CreateRoomingPerDayFailure()];
          }),
        );
      }),
    ),
  );

  updateRoomingPerDay$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomingPerDayDataStoreActions.UpdateRoomingPerDay),
      mergeMap((action) => {
        const params = {
          id: action.payload.id,
          body: action.payload.data,
        };
        return this.roomingPerDayApiService.updateById(<any>params).pipe(
          mergeMap(() => {
            this.notificationService.createNotification(
              'success',
              'Veri güncellendi',
              'Verileriniz başarıyla güncellendi.',
            );
            return [RoomingPerDayDataStoreActions.GetRoomingPerDays()];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while updating the data.',
            );
            return [RoomingPerDayDataStoreActions.UpdateRoomingPerDayFailure()];
          }),
        );
      }),
    ),
  );

  deleteRoomingPerDay$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoomingPerDayDataStoreActions.DeleteRoomingPerDay),
      mergeMap((action) => {
        const params = { id: action.payload.id };
        return this.roomingPerDayApiService.deleteById(params).pipe(
          mergeMap(() => {
            this.notificationService.createNotification(
              'success',
              'Success',
              'Your data has been deleted successfully.',
            );
            const payload: any = {
              id: action.payload.id,
            };
            return [
              RoomingPerDayDataStoreActions.DeleteRoomingPerDaySuccessful({
                payload,
              }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while deleting the data.',
            );
            return [RoomingPerDayDataStoreActions.DeleteRoomingPerDayFailure()];
          }),
        );
      }),
    ),
  );
}
